/**
 * Audio capture module.
 *
 * Captures microphone input, resamples to 16 kHz mono PCM via an
 * AudioWorkletProcessor, and delivers 250 ms chunks to a callback.
 *
 * The MediaStream is intentionally kept alive across session resets so we
 * never re-prompt the user for microphone permission.
 */

export interface AudioCaptureCallbacks {
  /** Called with each 250 ms PCM chunk (Int16, 16 kHz mono). */
  onChunk: (pcmBuffer: ArrayBuffer) => void;
  /** Called with RMS level [0–1] for each chunk, used for the visualizer. */
  onRms: (rms: number) => void;
}

export class AudioCapture {
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private callbacks: AudioCaptureCallbacks;
  private running = false;

  constructor(callbacks: AudioCaptureCallbacks) {
    this.callbacks = callbacks;
  }

  /**
   * Request microphone access and start streaming PCM chunks.
   * Safe to call multiple times — re-uses the existing MediaStream.
   */
  async start(): Promise<void> {
    if (this.running) return;

    // Acquire microphone only once; reuse on reconnect
    if (!this.mediaStream) {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 48000,
          channelCount: 1,
        },
      });
    }

    // AudioContext for capture (worklet runs at browser's native rate, ~48 kHz)
    this.audioContext = new AudioContext({ sampleRate: 48000 });

    await this.audioContext.audioWorklet.addModule("/pcm-processor.js");

    this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);
    this.workletNode = new AudioWorkletNode(this.audioContext, "pcm-processor");

    this.workletNode.port.onmessage = (event: MessageEvent) => {
      const { pcm, rms } = event.data as { pcm: ArrayBuffer; rms: number };
      this.callbacks.onChunk(pcm);
      this.callbacks.onRms(rms);
    };

    this.sourceNode.connect(this.workletNode);
    // Do NOT connect workletNode to destination — we don't want mic playback
    this.running = true;
  }

  /**
   * Stop the AudioWorklet and AudioContext but keep the MediaStream alive.
   * This allows reconnecting without a new permission prompt.
   */
  stopCapture(): void {
    this.running = false;
    this.workletNode?.disconnect();
    this.sourceNode?.disconnect();
    void this.audioContext?.close();
    this.workletNode = null;
    this.sourceNode = null;
    this.audioContext = null;
  }

  /**
   * Full teardown including the MediaStream.
   * Call only when the component unmounts entirely.
   */
  destroy(): void {
    this.stopCapture();
    this.mediaStream?.getTracks().forEach((t) => t.stop());
    this.mediaStream = null;
  }

  get isRunning(): boolean {
    return this.running;
  }
}
